import { bridgelessRestApiClient } from '@/api/clients'
import { buildPaginationParams } from '@/helpers'
import {
  PaginationParams,
  PaginationResponse,
  ProtobufAnyMessage,
} from '@/types'

export interface MultisigGroup {
  account: string
  members: string[]
  threshold: string
}

export interface MultisigGroupsResponse {
  group: MultisigGroup[]
  pagination: PaginationResponse
}

export type MultisigProposalStatus =
  | 'SUBMITTED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'EXECUTED'
  | 'FAILED'

export interface MultisigFinalTallyResult {
  yesCount: string
  noCount: string
}

export interface MultisigProposal {
  proposer: string
  id: string
  group: string
  submitBlock: string
  status: MultisigProposalStatus
  finalTallyResult?: MultisigFinalTallyResult
  votingEndBlock: string
  messages: ProtobufAnyMessage[]
}

export interface MultisigProposalsResponse {
  proposal: MultisigProposal[]
  pagination: PaginationResponse
}

export interface MultisigVote {
  proposalId: string
  voter: string
  option: 'YES' | 'NO'
  submitBlock: string
}

export interface MultisigVotesResponse {
  vote: MultisigVote[]
  pagination: PaginationResponse
}

export async function fetchMultisigGroups(
  pagination?: PaginationParams,
): Promise<MultisigGroupsResponse> {
  const { data } = await bridgelessRestApiClient.get<MultisigGroupsResponse>(
    '/cosmos/multisig/group',
    { params: buildPaginationParams(pagination) },
  )

  return data
}

export async function fetchMultisigGroupByAccount(
  account: string,
): Promise<MultisigGroup> {
  const { data } = await bridgelessRestApiClient.get<{
    group: MultisigGroup
  }>(`/cosmos/multisig/group/${account}`)

  return data.group
}

export async function fetchMultisigProposals(
  pagination?: PaginationParams,
): Promise<MultisigProposalsResponse> {
  const { data } = await bridgelessRestApiClient.get<MultisigProposalsResponse>(
    '/cosmos/multisig/proposal',
    { params: buildPaginationParams(pagination) },
  )

  return data
}

export async function fetchMultisigProposalVote(
  proposalId: string,
): Promise<MultisigVote> {
  const { data } = await bridgelessRestApiClient.get<{
    vote: MultisigVote
  }>(`/cosmos/multisig/vote/${proposalId}`)

  return data.vote
}

export async function fetchMultisigProposalVoteByVoter(
  proposalId: string,
  voter: string,
): Promise<MultisigVote> {
  const { data } = await bridgelessRestApiClient.get<{
    vote: MultisigVote
  }>(`/cosmos/multisig/vote/${proposalId}/${voter}`)

  return data.vote
}

export async function fetchMultisigProposalById(
  proposalId: string,
): Promise<MultisigProposal> {
  const { data } = await bridgelessRestApiClient.get<{
    proposal: MultisigProposal
  }>(`/cosmos/multisig/proposal/${proposalId}`)

  return data.proposal
}

export async function fetchVotesByProposal(
  proposalId: string,
  pagination?: PaginationParams,
): Promise<MultisigVotesResponse> {
  const { data } = await bridgelessRestApiClient.get<MultisigVotesResponse>(
    `/cosmos/multisig/vote/${proposalId}`,
    { params: buildPaginationParams(pagination) },
  )

  return data
}
